import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { ArrowLeft, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { VehicleTransfer, VehicleTransferStatus } from '../../../../shared/types/vehicle-transfer.types';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import DocumentList from '../../components/DocumentList';
import {
    customerTransferDetailStyles,
    customerTransferInfoRowStyles,
} from './TransferDetail.styles';

const statusConfig: Record<VehicleTransferStatus, { color: 'warning' | 'success' | 'error' | 'info' | 'default' }> = {
    pending: { color: 'warning' },
    approved: { color: 'success' },
    rejected: { color: 'error' },
    infoRequested: { color: 'info' },
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
    <Box sx={customerTransferInfoRowStyles.root}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
        {typeof value === 'string'
            ? <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined}>{value}</Typography>
            : value}
    </Box>
);

const CustomerTransferDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { get: getTransfer, edit: editTransfer } = useVehicleTransfer();
    const [transfer, setTransfer] = useState<VehicleTransfer | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmitForApproval = async () => {
        if (!id) return;
        setSubmitting(true);
        await editTransfer(id, { status: 'pending' });
        const updated = await getTransfer(id);
        if (updated) setTransfer(updated);
        setSubmitting(false);
    };

    useEffect(() => {
        if (!id) return;
        getTransfer(id).then((data) => {
            if (!data) { setNotFound(true); setLoading(false); return; }
            setTransfer(data);
            setLoading(false);
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <Box sx={customerTransferDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !transfer) {
        return <Alert severity="error">{t('transferDetail.notFound')}</Alert>;
    }

    const status = statusConfig[transfer.status ?? 'pending'];

    return (
        <Box sx={customerTransferDetailStyles.root}>
            <Box sx={customerTransferDetailStyles.header}>
                <IconButton component={Link} to="/app/garage" size="small">
                    <ArrowLeft size={20} />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>{t('transferDetail.title')}</Typography>
                <Chip label={t(`vehicleTransferStatus.${transfer.status ?? 'pending'}`)} color={status.color} size="small" />
                <Box sx={customerTransferDetailStyles.headerSpacer} />
                {transfer.status === 'infoRequested' && (
                    <Button
                        variant="contained"
                        startIcon={<Send size={16} />}
                        disabled={submitting}
                        onClick={handleSubmitForApproval}
                    >
                        {t('transferDetail.submitForApproval')}
                    </Button>
                )}
            </Box>

            {/* Stroj */}
            <Paper elevation={0} sx={customerTransferDetailStyles.card}>
                <Box sx={customerTransferDetailStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('transferDetail.machineTitle')}</Typography>
                </Box>
                <InfoRow label="VIN" value={transfer.vehicleUid} mono />
            </Paper>

            {/* Majitelé */}
            <Box sx={customerTransferDetailStyles.ownersGrid}>
                <Paper elevation={0} sx={customerTransferDetailStyles.card}>
                    <Box sx={customerTransferDetailStyles.cardHeader}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('transferDetail.fromOwnerTitle')}</Typography>
                    </Box>
                    <InfoRow label={t('transferDetail.nameLabel')} value={transfer.fromUserDisplayName ?? '—'} />
                </Paper>
                <Paper elevation={0} sx={customerTransferDetailStyles.card}>
                    <Box sx={customerTransferDetailStyles.cardHeader}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('transferDetail.toOwnerTitle')}</Typography>
                    </Box>
                    <InfoRow label={t('transferDetail.nameLabel')} value={transfer.toUserDisplayName ?? '—'} />
                </Paper>
            </Box>

            {transfer.status === 'rejected' && transfer.statusNote && (
                <Alert severity="error"><strong>{t('transferDetail.rejectedAlert')}</strong> {transfer.statusNote}</Alert>
            )}
            {transfer.status === 'infoRequested' && (
                <Alert severity="info">
                    {transfer.statusNote
                        ? <><strong>{t('transferDetail.infoRequestedAlert')}</strong> {transfer.statusNote}</>
                        : <strong>{t('transferDetail.infoRequestedAlertNoNote')}</strong>
                    }
                </Alert>
            )}

            {/* Žádost */}
            <Paper elevation={0} sx={customerTransferDetailStyles.card}>
                <Box sx={customerTransferDetailStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('transferDetail.requestTitle')}</Typography>
                </Box>
                <InfoRow label={t('transferDetail.requestDate')} value={formatDate(transfer.createdAt)} />
                <Divider />
                <InfoRow label={t('transferDetail.documentsLabel')} value={<DocumentList filters={{ vehicleTransferUid: id }} uploadData={{ vehicleUid: transfer.vehicleUid, vehicleTransferUid: id }} />} />
            </Paper>
        </Box>
    );
};

export default CustomerTransferDetail;
